import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import type {
  SyncAckMessage,
  SyncConflictMessage,
  SyncDownloadCompleteMessage,
  SyncDownloadMessage,
  SyncMessage,
  SyncMigrationMessage,
  SyncRepairRequestMessage,
  SyncSchemaMessage,
  SyncTokenRefreshMessage,
} from '@checkpoint/shared';
import { nanoid } from 'nanoid';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { ChangeApplierService } from './change-applier.service';
import { ClockSyncService } from './clock-sync.service';
import { DatabaseService } from './database.service';
import { LoggerService } from './logger.service';
import { MigrationApplierService } from './migration-applier.service';
import { TabFocusService } from './tab-focus.service';
import { ToastService } from './toast.service';
import { LOCAL_STORAGE } from './user-storage.service';

export type SyncConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'error'
  | 'offline'
  | 'follower'
  | 'paused';

/**
 * BroadcastChannel message types for cross-tab communication
 */
type BroadcastMessage =
  | { type: 'leader-elected'; leaderId: string }
  | { type: 'data-changed'; table: string; operation: string; rowIds: string[] }
  | { type: 'tab-focused'; tabId: string } // this tab has gained focus
  | { type: 'request-leadership'; requesterId: string }; // explicit "Use this tab" button

/**
 * Custom sync service - Phase 5: Offline support and reconnection
 */
@Injectable({
  providedIn: 'root',
})
export class CustomSyncService {
  private ws: WebSocket | null = null;
  private clientId: string;
  private clockSync = inject(ClockSyncService);
  private changeApplier = inject(ChangeApplierService);
  private migrationApplier = inject(MigrationApplierService);
  private authService = inject(AuthService);
  private tabFocusService = inject(TabFocusService);
  private toast = inject(ToastService);
  private log = inject(LoggerService).getLogger('custom-sync');
  private databaseService = inject(DatabaseService);
  private localStorage = inject(LOCAL_STORAGE);

  private readonly syncObservabilityKeys = {
    verifyBatchFailures: 'cp:sync:obs:verify_batch_failures',
    verifyChecksumMismatches: 'cp:sync:obs:verify_checksum_mismatches',
    tableDigestMismatches: 'cp:sync:obs:table_digest_mismatches',
    forcedFullResyncCount: 'cp:sync:obs:forced_full_resync_count',
    lastIntegrityReason: 'cp:sync:obs:last_integrity_reason',
    lastIntegrityAt: 'cp:sync:obs:last_integrity_at',
  } as const;

  // Multi-tab coordination
  private channel?: BroadcastChannel;
  private tabId: string;
  private isLeader = false;
  private lockReleaseCallback?: () => void;
  private leadershipCheckTimer?: number;
  private leadershipCheckDelay = 5_000; // starts at 5s; doubles on each failed attempt up to max
  private readonly LEADERSHIP_CHECK_MIN_DELAY = 5_000;
  private readonly LEADERSHIP_CHECK_MAX_DELAY = 60_000;
  private readonly LEADER_TAB_KEY = 'checkpoint-leader-tab-id';
  // _sync_metadata key recording that repairUnTrackedBlobs() has run on this database
  private readonly BLOB_SYNC_REPAIR_KEY = 'blob_sync_repair_done';
  private wasEverPaused = false; // true when this tab was paused after losing leadership; triggers page reload on leadership gain

  private resyncTimer: any;
  private uploadDebounceTimer?: number;

  // Bound method references for event listeners (prevents stack-up bugs)
  private boundOnline?: () => void;
  private boundOffline?: () => void;
  private boundDbChange?: (event: Event) => void;

  // Reconnection state
  private reconnectAttempt = 0;
  private maxReconnectAttempts = 10;
  private reconnectTimer?: number;
  // Set to true by stop() so that late-firing WebSocket onclose events don't
  // trigger scheduleReconnect() after the user has intentionally logged out.
  private isStopped = false;

  // Connection state signals
  readonly connectionState = signal<SyncConnectionState>('disconnected');
  readonly isConnected = computed(() => this.connectionState() === 'connected');
  readonly isOffline = computed(() => this.connectionState() === 'offline');
  readonly isPaused = computed(() => this.connectionState() === 'paused');

  // Legacy state signals (kept for interface compatibility)
  readonly connected = computed(() => this.connectionState() === 'connected');
  readonly syncing = signal(false);
  readonly error = signal<string | null>(null);
  readonly pendingChanges = signal(0);
  readonly showResetDialog = signal(false); // Database reset dialog (not used in custom sync yet)

  // Message queue for sequential processing
  private messageQueue: SyncMessage[] = [];
  private processingQueue = false;

  // Schema initialization tracking
  private schemaApplied = false;
  private schemaPromise: Promise<void> | null = null;
  private resolveSchema?: () => void;
  private rejectSchema?: (error: Error) => void;
  private startupPromise?: Promise<void>; // Track ongoing startup to make start() idempotent

  // Initial download tracking
  private downloadComplete = false;
  private downloadPromise: Promise<void> | null = null;
  private resolveDownload?: () => void;
  private rejectDownload?: (error: Error) => void;

  // Connection promise tracking (used by connectAndWait)
  private resolveConnection?: () => void;
  private rejectConnection?: (error: Error) => void;
  private repairAttemptedThisCycle = false;

  // Init message tracking (prevent duplicate sends)
  private initMessageSent = false;

  // Full-sync reconciliation: track received rows during initial download
  private fullSyncReceivedRows: Map<string, Set<string>> | null = null;

  constructor() {
    this.clientId = this.getOrCreateClientId();
    const storedTabId = sessionStorage.getItem('checkpoint-tab-id');
    if (storedTabId) {
      this.tabId = storedTabId;
    } else {
      this.tabId = `tab-${nanoid()}`;
      sessionStorage.setItem('checkpoint-tab-id', this.tabId);
    }
    this.log.info('Service initialized, client_id:', this.clientId, 'tab_id:', this.tabId);

    // Create promise that will resolve when schema is applied
    this.schemaPromise = new Promise<void>((resolve, reject) => {
      this.resolveSchema = resolve;
      this.rejectSchema = reject;
    });

    // Create promise that will resolve when initial download completes
    this.downloadPromise = new Promise<void>((resolve, reject) => {
      this.resolveDownload = resolve;
      this.rejectDownload = reject;
    });

    // Create bound method references for event listeners (prevents stack-up on multiple starts)
    this.boundOnline = () => this.handleOnline();
    this.boundOffline = () => this.handleOffline();
    this.log.info('🏷️ Tab identity set — tab_id:', this.tabId, 'client_id:', this.clientId);
    this.boundDbChange = (event: Event) => {
      // Broadcast local changes to follower tabs
      const customEvent = event as CustomEvent;
      if (customEvent.detail && this.isLeader && this.channel) {
        const { table, operation, rowIds } = customEvent.detail;
        this.log.info('Broadcasting local change to followers:', { table, operation, rowIds });
        this.channel.postMessage({
          type: 'data-changed',
          table,
          operation,
          rowIds,
        });
      }
      this.triggerUpload();
    };

    // Initialize BroadcastChannel for multi-tab coordination (if available)
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel('checkpoint-sync');
      this.channel.onmessage = (event) => this.handleBroadcastMessage(event.data);
    } else {
      this.log.warn('BroadcastChannel not available - multi-tab coordination disabled');
    }

    // React to focus changes from TabFocusService.
    // When this tab gains focus and is paused/follower:
    //   1. Broadcast 'tab-focused' so the current leader knows to yield.
    //   2. Schedule a fast lock-acquisition attempt (500ms later) — by then
    //      the leader should have released the Web Lock.
    // The leader tab does NOT monitor its own focus loss; it only yields when
    // it receives a 'tab-focused' broadcast from a different tab.
    effect(() => {
      const focused = this.tabFocusService.isFocused();
      // Also track connectionState so the effect fires when the state transitions
      // to 'paused' (e.g. doStart() ran while this tab was already focused).
      // databaseService.isPaused is the ground truth but is NOT reactive; reading
      // connectionState() here gives us the reactive trigger we need.
      const state = this.connectionState();
      const isPaused = this.databaseService.isPaused;
      this.log.debug(
        `[TabFocus] isFocused=${focused} dbPaused=${isPaused} isLeader=${this.isLeader} state=${state}`,
      );

      if (focused && isPaused) {
        untracked(() => {
          this.log.info(
            'Tab gained focus while DB is paused — broadcasting tab-focused so leader can yield',
          );
          if (this.channel) {
            this.channel.postMessage({ type: 'tab-focused', tabId: this.tabId });
          }
          // Reset backoff and schedule a quick lock attempt after a short delay
          // so we acquire the lock soon after the leader releases it.
          if (this.leadershipCheckTimer) {
            clearTimeout(this.leadershipCheckTimer);
            this.leadershipCheckTimer = undefined;
          }
          this.leadershipCheckDelay = this.LEADERSHIP_CHECK_MIN_DELAY;
          this.leadershipCheckTimer = window.setTimeout(async () => {
            this.leadershipCheckTimer = undefined;
            await this.start();
          }, 500) as unknown as number;
        });
      }
    });
  }

  /**
   * Start sync service and wait for connection to be established
   * CRITICAL: This waits for schema to be applied before resolving
   *
   * This method is idempotent - calling it multiple times will return
   * the same promise and wait for the same initialization to complete.
   */
  async start(): Promise<void> {
    // If already started and completed, return immediately
    if (this.schemaApplied && this.connectionState() === 'connected') {
      this.log.debug('Sync already fully initialized');
      return;
    }

    // If startup is in progress, return the existing promise
    if (this.startupPromise) {
      this.log.debug('Startup already in progress - waiting for existing promise');
      return this.startupPromise;
    }

    // Create and store the startup promise
    this.startupPromise = this.doStart();

    try {
      await this.startupPromise;
    } finally {
      // Clear the promise once complete (success or failure)
      this.startupPromise = undefined;
    }
  }

  /**
   * Internal method that performs the actual startup
   */
  private async doStart(): Promise<void> {
    const startTime = performance.now();
    this.log.info('🚀 Starting custom-sync...');

    // Clear the stopped flag so reconnection logic is re-enabled for this session
    this.isStopped = false;

    // Re-create BroadcastChannel if it was destroyed by a prior stop() call.
    // The channel is only created in the constructor (which runs once for the
    // root-provided service), so stop() → start() cycles leave it null.
    if (!this.channel && typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel('checkpoint-sync');
      this.channel.onmessage = (event) => this.handleBroadcastMessage(event.data);
      this.log.info('♻️ Re-created BroadcastChannel after prior stop()');
    }

    // FIX: If we already hold the Web Lock from a prior doStart() that acquired
    // it but failed later (e.g. connectAndWait timeout, dead worker on iOS
    // resume), skip re-acquisition. Web Locks are NOT re-entrant — requesting
    // a lock we already hold with ifAvailable:true returns null, which would
    // incorrectly put this single-tab PWA into "paused" mode.
    let becameLeader: boolean;
    if (this.lockReleaseCallback) {
      this.log.info('🔒 Already holding Web Lock from prior attempt — proceeding as leader');
      becameLeader = true;
      // Clean up stale reconnect timers / WebSocket from the failed prior attempt
      this.disconnect();
    } else {
      becameLeader = await this.tryBecomeLeader();
    }

    if (!becameLeader) {
      this.log.info('Another tab is sync leader — pausing and waiting');
      // Only pause the DB if we actually lost the lock race
      await this.databaseService.pauseForLeadership();
      this.connectionState.set('paused');
      this.wasEverPaused = true;
      // Focus tracking is handled by the effect() in the constructor which
      // reacts to TabFocusService.isFocused() changes.  If this tab is already
      // focused when connectionState becomes 'paused', the effect will fire,
      // broadcast 'tab-focused' to the leader, and schedule a 500ms start() attempt.
      this.log.info(
        'Paused | hasFocus:',
        document.hasFocus(),
        '| hidden:',
        document.hidden,
        '| state:',
        document.visibilityState,
      );

      this.watchForLeadershipOpportunity();
      return;
    }

    // === Won leadership ===
    localStorage.setItem(this.LEADER_TAB_KEY, this.tabId);
    // NOTE: isLeader is set to true only AFTER full initialization (at the end of doStart()).
    // Setting it here would allow yieldLeadership() to be called while the DB is still
    // mid-init (uninitialized), causing pauseForLeadership() to run concurrently with
    // sendInitMessage/handleOpen, resulting in "Database not initialized" errors.

    // If this tab was previously paused (it lost the lock on a prior attempt), reload the page
    // so that Angular re-runs all route guards and resolvers against the now-live database.
    // Release the Web Lock BEFORE reloading so the reloaded page can win it immediately with
    // ifAvailable:true instead of racing against the old page's still-alive browsing context.
    // This is safe because pauseForLeadership() already terminated the DB worker, meaning no
    // OPFS SAH handles are held — there is no ordering hazard here.
    if (this.wasEverPaused) {
      this.log.info('🔄 Tab won leadership from paused state — releasing lock then reloading page');
      if (this.lockReleaseCallback) {
        const release = this.lockReleaseCallback;
        this.lockReleaseCallback = undefined;
        release();
      }
      window.location.reload();
      return;
    }

    // Ensure DB is in a runnable state (no-op if it never was paused)
    await this.databaseService.resumeAfterLeadership();

    if (this.channel) {
      this.channel.postMessage({ type: 'leader-elected', leaderId: this.tabId });
    }
    this.log.info('✅ Became sync leader');

    // Connect and wait for actual connection
    this.log.debug(
      `T+${(performance.now() - startTime).toFixed(0)}ms: Calling connectAndWait()...`,
    );
    await this.connectAndWait();
    this.log.debug(`T+${(performance.now() - startTime).toFixed(0)}ms: connectAndWait() RETURNED`);

    // Check if database already has tables (existing database)
    // For existing databases: skip waiting for schema DDL from server
    // For new databases: wait for server to send schema DDL message
    this.log.debug(
      `T+${(performance.now() - startTime).toFixed(0)}ms: BEFORE database check (schemaApplied=${this.schemaApplied})`,
    );
    try {
      this.log.debug('Checking if database has tables: SELECT name FROM sqlite_master...');
      const tables = await this.databaseService.exec(
        "SELECT name FROM sqlite_master WHERE type='table' AND name='organization'",
      );
      this.log.debug('Database check result:', { tables, length: tables?.length });

      if (tables && tables.length > 0) {
        this.log.info('✅ Existing database detected - schema already present');
        if (!this.schemaApplied) {
          this.log.debug('Marking schema as applied (existing database)');
          this.schemaApplied = true;
          if (this.resolveSchema) {
            this.resolveSchema();
            this.log.debug('✅ Schema promise resolved (existing database)');
          } else {
            this.log.warn('⚠️ resolveSchema callback is null!');
          }
        } else {
          this.log.debug('schemaApplied was already true');
          // Re-resolve the schema promise in case it was reset by resetAllPromises().
          // Without this, a reset promise stays unresolved and doStart() hangs indefinitely
          // because the server never re-sends schema DDL for a DB that already has a schema.
          if (this.resolveSchema) {
            this.resolveSchema();
            this.log.debug(
              '✅ Schema promise re-resolved (schema was already applied from a prior cycle)',
            );
          }
        }
      } else {
        this.log.info('📋 New database detected - will wait for schema DDL from server');
      }
    } catch (err) {
      this.log.error('❌ Database check FAILED with error:', err);
      this.log.error('Error details:', {
        name: (err as Error).name,
        message: (err as Error).message,
        stack: (err as Error).stack,
      });
    }
    this.log.debug(
      `T+${(performance.now() - startTime).toFixed(0)}ms: AFTER database check (schemaApplied=${this.schemaApplied}, hasResolveCallback=${!!this.resolveSchema})`,
    );

    // CRITICAL: Wait for schema to be applied (new database) or immediately proceed (existing database)
    const schemaWaitStart = performance.now();
    if (!this.schemaApplied) {
      // New database - waiting for server to send schema DDL
      this.log.info('⏳ Waiting for schema DDL from server (new database)...');
    } else {
      // Existing database - schema already exists, promise already resolved
      this.log.debug('Schema already applied (existing database) - proceeding immediately');
    }

    try {
      await this.schemaPromise;
    } catch (schemaError: any) {
      if (
        typeof schemaError?.message === 'string' &&
        schemaError.message.includes('Sync service stopped - schema promise cancelled')
      ) {
        this.log.info(
          'Schema wait cancelled during intentional sync reset/reconnect; allowing startup to restart cleanly',
        );
        return;
      }
      throw schemaError;
    }
    const schemaWaitMs = performance.now() - schemaWaitStart;
    const schemaWaitTime = schemaWaitMs.toFixed(0);

    if (schemaWaitMs > 5) {
      // Only log if we actually waited (new database flow)
      this.log.info(
        `T+${(performance.now() - startTime).toFixed(0)}ms: ✅ Schema DDL applied (waited ${schemaWaitTime}ms)`,
      );
    } else {
      this.log.debug(
        `T+${(performance.now() - startTime).toFixed(0)}ms: ✅ Schema check complete (${schemaWaitTime}ms)`,
      );
    }

    // CRITICAL: Wait for initial download to complete before resolving
    this.log.debug(
      `T+${(performance.now() - startTime).toFixed(0)}ms: BEFORE waiting for download promise (downloadComplete=${this.downloadComplete}, hasResolveCallback=${!!this.resolveDownload})...`,
    );
    this.log.info('⏳ Waiting for initial download to complete...');

    // Add timeout to prevent hanging forever if download_complete never arrives
    // NOTE: Timeout is generous (60s) to accommodate large initial sync datasets
    // TODO: Consider making sync non-blocking and showing loading UI instead
    const downloadTimeout = new Promise<void>((_, reject) => {
      setTimeout(
        () =>
          reject(
            new Error(
              'Initial download timeout - download_complete message not received after 60s',
            ),
          ),
        60000,
      );
    });

    try {
      const downloadWaitStart = performance.now();
      await Promise.race([this.downloadPromise, downloadTimeout]);
      const downloadWaitTime = (performance.now() - downloadWaitStart).toFixed(0);
      this.log.debug(`Download promise resolved after ${downloadWaitTime}ms`);
    } catch (error: any) {
      if (
        typeof error?.message === 'string' &&
        error.message.includes('Sync service stopped - download promise cancelled')
      ) {
        this.log.info(
          'Download wait cancelled during intentional sync reset/reconnect; allowing startup to restart cleanly',
        );
        return;
      }
      this.log.error('❌ Download timeout:', error.message);
      throw error;
    }

    this.log.info(
      `T+${(performance.now() - startTime).toFixed(0)}ms: ✅ Initial download complete - ALL DATA SYNCED`,
    );

    // Listen for online/offline events AFTER everything is initialized
    // Use bound method references to prevent listener stack-up on multiple start/stop cycles
    if (this.boundOnline) {
      window.addEventListener('online', this.boundOnline);
    }
    if (this.boundOffline) {
      window.addEventListener('offline', this.boundOffline);
    }
    if (this.boundDbChange) {
      window.addEventListener('db-change-tracked', this.boundDbChange);
    }

    // One-time repair: backfill _sync_log for any form_blobs rows that were
    // never tracked (due to the INSERT OR IGNORE bug fixed in Feb 2026).
    await this.repairUnTrackedBlobs();

    // Recovery: if any blobs referenced in local records are missing from
    // form_blobs, the cursor skipped them on a previous connection. Reset to 0
    // and reconnect so the server resends the missing rows.
    const hasMissingBlobs = await this.repairMissingReferencedBlobs();
    if (hasMissingBlobs) {
      this.log.warn(
        '⚠️ Missing referenced blobs detected — resetting sync cursor to 0 and reconnecting',
      );
      await this.updateLastLogId(0);
      this.resetAllPromises();
      this.disconnect(); // handleClose will schedule reconnect
      return;
    }

    // Upload any pending changes that accumulated while offline or during startup
    // Mark as leader now that initialization is fully complete.
    // Doing this at the end (rather than at lock-win time) prevents yieldLeadership() from
    // being triggered by a concurrent request-leadership broadcast while the DB is still
    // mid-init (uninitialized after resumeAfterLeadership), which would re-pause the DB
    // and cause "Database not initialized" errors in sendInitMessage.
    this.isLeader = true;
    await this.databaseService.refreshAllQueries();
    this.triggerUpload();
  }

  /**
   * Repair: insert _sync_log entries for any form_blobs rows that are not yet
   * queued for sync. Runs on every connect so you can observe it in logs.
   * Once blobs are synced (synced=1 in _sync_log) they won't be re-queued.
   */
  private async repairUnTrackedBlobs(): Promise<void> {
    try {
      // Runs once per database. Blobs downloaded from the server also have no
      // _sync_log entry, so re-running this would re-upload every one of them.
      const done = await this.databaseService.exec(
        `SELECT value FROM _sync_metadata WHERE key = ?`,
        [this.BLOB_SYNC_REPAIR_KEY],
      );
      if (done?.length > 0) {
        return;
      }

      // Find blobs that have no entry in _sync_log at all
      const untracked = await this.databaseService.exec(
        `SELECT hash, data, mime_type, size_bytes, ref_count, created_at
         FROM form_blobs
         WHERE hash NOT IN (
           SELECT row_id FROM _sync_log WHERE table_name = 'form_blobs'
         )`,
      );

      if (untracked && untracked.length > 0) {
        this.log.info(
          `🔧 blob_sync_repair: backfilling ${untracked.length} untracked blob(s) into _sync_log`,
        );
        for (const blob of untracked) {
          const rowData = JSON.stringify({
            hash: blob['hash'],
            data: blob['data'],
            mime_type: blob['mime_type'],
            size_bytes: blob['size_bytes'],
            ref_count: blob['ref_count'],
            created_at: blob['created_at'],
          });
          await this.databaseService.exec(
            `INSERT INTO _sync_log (table_name, row_id, operation, row_data, timestamp, synced)
             VALUES ('form_blobs', ?, 'INSERT', ?, ?, 0)`,
            [blob['hash'], rowData, blob['created_at'] || Date.now()],
          );
        }
        this.log.info(`🔧 blob_sync_repair: done — ${untracked.length} blob(s) queued for upload`);
      } else {
        this.log.debug('blob_sync_repair: no untracked blobs found');
      }
      await this.markBlobSyncRepairDone();
    } catch (err) {
      // Non-fatal: log and continue — upload will still proceed with whatever is in _sync_log
      this.log.error('blob_sync_repair failed (non-fatal):', err);
    }
  }

  private async markBlobSyncRepairDone(): Promise<void> {
    try {
      await this.databaseService.exec(
        `INSERT OR IGNORE INTO _sync_metadata (key, value) VALUES (?, ?)`,
        [this.BLOB_SYNC_REPAIR_KEY, String(Date.now())],
      );
    } catch (err) {
      this.log.error('Failed to record blob_sync_repair as done (non-fatal):', err);
    }
  }

  /**
   * Recovery check: scan local interaction_records for blob:sha256: references
   * and return true if any referenced blob hash is missing from form_blobs.
   * This catches the case where the sync cursor was advanced past blob rows
   * that this client never actually received.
   */
  private async repairMissingReferencedBlobs(): Promise<boolean> {
    try {
      // Find records that reference blobs
      const records = await this.databaseService.exec(
        `SELECT data FROM interaction_records WHERE data LIKE '%blob:sha256:%'`,
      );

      if (!records || records.length === 0) {
        return false;
      }

      // Extract all referenced full-length SHA-256 hashes
      const referencedHashes = new Set<string>();
      for (const record of records) {
        const dataStr = record['data'] as string;
        const matches = dataStr.matchAll(/blob:sha256:([a-f0-9]{64})/g);
        for (const match of matches) {
          referencedHashes.add(match[1]);
        }
      }

      if (referencedHashes.size === 0) {
        return false;
      }

      // Check which hashes exist locally in form_blobs
      const hashes = [...referencedHashes];
      const placeholders = hashes.map(() => '?').join(',');
      const existingResult = await this.databaseService.exec(
        `SELECT hash FROM form_blobs WHERE hash IN (${placeholders})`,
        hashes,
      );

      const existingHashes = new Set((existingResult || []).map((r) => r['hash'] as string));
      const missingHashes = hashes.filter((h) => !existingHashes.has(h));

      if (missingHashes.length > 0) {
        this.log.warn(
          `⚠️ blob_sync_repair: ${missingHashes.length} blob(s) referenced in records but missing locally: ` +
            missingHashes.map((h) => h.substring(0, 8)).join(', '),
        );
        return true;
      }

      return false;
    } catch (err: any) {
      this.log.error('repairMissingReferencedBlobs failed (non-fatal):', err);
      return false;
    }
  }

  /**
   * Stop sync service and reset ALL state for next user/login
   *
   * CRITICAL: This must completely reset all internal state to prevent
   * state leakage between users or login transitions. Each field has a specific
   * reason for being reset to prevent "no such table: users" and similar errors.
   */
  stop(): void {
    this.log.info('Stopping sync service - beginning comprehensive state reset');

    // Mark as stopped BEFORE disconnecting so that the async WebSocket
    // onclose event (which fires after ws.close()) does not schedule a
    // reconnect via handleClose().
    this.isStopped = true;

    // Step 1: Disconnect WebSocket (prevents further messages)
    this.disconnect();

    // Step 2: Remove ALL event listeners using bound references
    // CRITICAL: Must use the SAME bound references that were added, or removal won't work
    this.log.debug('Removing event listeners');
    if (this.boundOnline) {
      window.removeEventListener('online', this.boundOnline);
    }
    if (this.boundOffline) {
      window.removeEventListener('offline', this.boundOffline);
    }
    if (this.boundDbChange) {
      window.removeEventListener('db-change-tracked', this.boundDbChange);
    }

    // Step 3: Reset promise state (CRITICAL - reject existing, don't create new)
    // This prevents hanging promises from previous login session
    this.log.debug('Resetting promise states');
    this.resetAllPromises();

    // Step 4: Reset schema state
    // Reason: schemaApplied controls db.init() flow - must reset for new user/database
    this.schemaApplied = false;
    this.initMessageSent = false; // Prevent duplicate init messages on reconnect
    this.startupPromise = undefined; // Clear startup tracking

    // Step 5: Reset download state
    // Reason: downloadComplete blocks app startup - must reset for new user/database
    this.downloadComplete = false;

    // Step 6: Reset message processing state
    // Reason: Queued messages could be processed after stop() and use old database
    this.messageQueue = [];
    this.processingQueue = false;
    this.fullSyncReceivedRows = null; // Clear full-sync tracking

    // Step 7: Reset connection/reconnection state
    // Reason: stale reconnect timers could try to connect after user switches
    this.reconnectAttempt = 0; // Reset backoff for next login
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }

    // Step 8: Reset sync signals
    // Reason: UI might still read these signals after logout
    this.connectionState.set('disconnected');
    this.syncing.set(false);
    this.error.set(null);
    this.pendingChanges.set(0);

    // Step 9: Clean up multi-tab resources
    // Reason: Leader state must be cleared to allow next tab to become leader
    this.isLeader = false;
    localStorage.removeItem(this.LEADER_TAB_KEY);
    if (this.leadershipCheckTimer) {
      clearTimeout(this.leadershipCheckTimer);
      this.leadershipCheckTimer = undefined;
    }
    this.leadershipCheckDelay = this.LEADERSHIP_CHECK_MIN_DELAY;
    if (this.lockReleaseCallback) {
      this.lockReleaseCallback();
      this.lockReleaseCallback = undefined;
    }
    // NOTE: BroadcastChannel is intentionally NOT closed here.
    // doStart() re-creates it if needed, but leaving it open avoids the
    // bug where requestLeadership() silently fails after stop() + start()
    // because the channel was destroyed and never re-created.

    // Step 10: Stop any pending timers
    // Reason: timers could fire after logout and cause errors
    this.stopTimers();

    this.log.info('✅ Sync service stopped - all state reset complete (ready for next user)');
  }

  /**
   * Reset all promise state - reject existing promises AND CREATE NEW FRESH ONES
   *
   * CRITICAL FIX for Bug #1 (Stale Promise Reuse):
   * - User 1's promises are resolved and stored
   * - User 1 logs out, stop() rejects existing promises
   * - User 2 logs in, doStart() tries to await promises
   * - Problem: Promises from User 1 are already resolved OR null
   * - Solution: After rejecting, CREATE NEW PROMISES for User 2
   *
   * This prevents User 2 from awaiting stale promises from User 1's session.
   */
  private resetAllPromises(): void {
    // Schema promise - reject existing, then CREATE NEW
    if (this.rejectSchema) {
      try {
        this.rejectSchema(new Error('Sync service stopped - schema promise cancelled'));
      } catch (e) {
        // Ignoring errors from rejection
      }
    }
    // CREATE A NEW PROMISE for the next user
    this.schemaPromise = new Promise<void>((resolve, reject) => {
      this.resolveSchema = resolve;
      this.rejectSchema = reject;
    });
    // Prevent unhandled rejection if the promise is rejected before doStart() awaits it
    this.schemaPromise.catch(() => {});

    // Download promise - reject existing, then CREATE NEW
    if (this.rejectDownload) {
      try {
        this.rejectDownload(new Error('Sync service stopped - download promise cancelled'));
      } catch (e) {
        // Ignoring errors from rejection
      }
    }
    // CREATE A NEW PROMISE for the next user
    this.downloadPromise = new Promise<void>((resolve, reject) => {
      this.resolveDownload = resolve;
      this.rejectDownload = reject;
    });

    // Connection promise - reject existing, then CREATE NEW
    if (this.rejectConnection) {
      try {
        this.rejectConnection(new Error('Sync service stopped - connection promise cancelled'));
      } catch (e) {
        // Ignoring errors from rejection
      }
    }
    // CREATE A NEW PROMISE for the next user (will be resolved in handleOpen)
    // Note: We don't store this in a variable, but create it here so resolve/reject work
  }

  /**
   * Connect to sync server and wait for connection to be established
   */
  private async connectAndWait(): Promise<void> {
    const connectTime = performance.now();

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.log.warn('Already connected');
      return;
    }

    this.log.debug(`T+${(performance.now() - connectTime).toFixed(0)}ms: connectAndWait() CALLED`);
    this.connectionState.set('connecting');
    this.log.info('Connecting to sync server...');

    return new Promise<void>((resolve, reject) => {
      this.log.debug(
        `T+${(performance.now() - connectTime).toFixed(0)}ms: Storing resolve/reject callbacks`,
      );
      // Store resolve/reject so handleSchema can call them
      this.resolveConnection = resolve;
      this.rejectConnection = reject;

      const timeout = setTimeout(() => {
        this.log.error('Connection timeout');
        this.connectionState.set('error');
        this.error.set('Connection timeout');
        reject(new Error('Connection timeout'));
      }, 10000); // 10 second timeout

      try {
        this.log.debug(`T+${(performance.now() - connectTime).toFixed(0)}ms: Checking auth token`);
        const token = this.authService.getToken();
        if (!token) {
          this.log.warn(`T+${(performance.now() - connectTime).toFixed(0)}ms: No auth token found`);
          clearTimeout(timeout);
          reject(new Error('No authentication token'));
          return;
        }

        // Convert HTTP URL to WebSocket URL
        const wsUrl =
          environment.apiUrl.replace(/^http/, 'ws').replace('/api', '') + '/cp2-ws?token=' + token;
        this.log.debug(
          `T+${(performance.now() - connectTime).toFixed(0)}ms: Creating WebSocket to: ${wsUrl.substring(0, 50)}...`,
        );
        this.log.info('🔗 Attempting WebSocket connection to:', wsUrl);

        this.ws = new WebSocket(wsUrl);
        this.log.debug(
          `T+${(performance.now() - connectTime).toFixed(0)}ms: WebSocket object created, state=${this.ws.readyState}`,
        );

        this.ws.onopen = () => {
          this.log.info(`T+${(performance.now() - connectTime).toFixed(0)}ms: WEBSOCKET OPENED`);
          this.log.debug(
            `T+${(performance.now() - connectTime).toFixed(0)}ms: Connection promise state: hasResolveCallback=${!!resolve}, hasRejectCallback=${!!reject}`,
          );
          clearTimeout(timeout);

          // handleOpen just does initial setup (clock sync)
          // Connection is established - resolve immediately
          // Schema waiting happens separately in doStart()
          this.log.debug(
            `T+${(performance.now() - connectTime).toFixed(0)}ms: Calling handleOpen()...`,
          );
          this.handleOpen()
            .then(() => {
              this.log.debug(
                `T+${(performance.now() - connectTime).toFixed(0)}ms: handleOpen() complete - connection established`,
              );
              this.log.debug(
                `T+${(performance.now() - connectTime).toFixed(0)}ms: Schema state: schemaApplied=${this.schemaApplied}, hasSchemaResolve=${!!this.resolveSchema}`,
              );

              // CRITICAL FIX: Resolve connection promise immediately
              // For existing databases, server sends download_complete without schema message
              // Schema waiting happens separately in doStart() via schema/download promises
              if (this.resolveConnection) {
                this.log.debug(
                  `T+${(performance.now() - connectTime).toFixed(0)}ms: Resolving connection promise`,
                );
                this.resolveConnection();
                this.resolveConnection = undefined;
              }
            })
            .catch((err) => {
              this.log.error(
                `T+${(performance.now() - connectTime).toFixed(0)}ms: handleOpen() failed:`,
                err,
              );
              this.connectionState.set('error');
              this.error.set(err.message || 'Connection failed');
              reject(err);
            });
        };

        this.ws.onclose = () => {
          this.log.debug('WebSocket closed');
          this.handleClose();
        };

        this.ws.onerror = (err) => {
          this.log.error('WebSocket error:', err);
          clearTimeout(timeout);
          this.handleError(err);
          reject(new Error('WebSocket error'));
        };

        this.ws.onmessage = (event) => {
          this.log.debug('Received message:', event.data.substring(0, 200) + '...');
          const msg = JSON.parse(event.data);

          // Add message to queue instead of processing directly
          this.messageQueue.push(msg);
          this.processMessageQueue();
        };
      } catch (error: any) {
        this.log.error('Connection setup failed:', error);
        clearTimeout(timeout);
        this.log.error('Connection failed:', error);
        this.connectionState.set('error');
        this.error.set(error.message);
        reject(error);
      }
    });
  }

  /**
   * Connect to sync server (legacy method, kept for internal use)
   */
  private async connect(): Promise<void> {
    return this.connectAndWait();
  }

  /**
   * Handle connection open
   */
  private async handleOpen(): Promise<void> {
    // Safety net: if the WebSocket somehow opened while DB is paused
    // (e.g. a reconnect timer fired before it was cleared), close it
    // immediately — sendInitMessage would crash on exec().
    if (this.databaseService.isPaused) {
      this.log.warn('⏸ handleOpen called while DB is paused — closing WebSocket');
      this.ws?.close();
      return;
    }

    this.log.info('✅ Connected to sync server');
    this.connectionState.set('connected');
    this.reconnectAttempt = 0;
    this.error.set(null);
    this.repairAttemptedThisCycle = false;

    // CRITICAL FIX for Bug #2 (fullSyncReceivedRows Race Condition):
    // Initialize fullSyncReceivedRows IMMEDIATELY when connection opens
    // This ensures it's ready BEFORE ANY MESSAGES start processing
    // If we wait until sendInitMessage(), there's a race where download messages
    // might arrive before fullSyncReceivedRows is initialized, causing them to be ignored
    this.fullSyncReceivedRows = new Map();
    this.log.debug('✅ Initialized fullSyncReceivedRows for new sync session');

    // CRITICAL: Sync clock first
    await this.clockSync.sync(this.ws!);

    // Send initialization message to trigger schema/data sync
    // Only send once per connection to prevent duplicate syncs
    if (!this.initMessageSent) {
      await this.sendInitMessage();
      this.initMessageSent = true;
    } else {
      this.log.debug('Init message already sent - skipping duplicate send');
    }

    // Upload any pending changes that accumulated while offline
    this.triggerUpload();

    // Start periodic clock resync
    this.startResyncTimer();
  }

  /**
   * Handle connection close
   */
  private handleClose(): void {
    this.log.warn('❌ Disconnected from sync server');
    this.stopTimers();

    // Reset init message flag so reconnection can send it again
    this.initMessageSent = false;

    // Do NOT schedule a reconnect if the service has been deliberately stopped
    // (e.g. during logout). The WebSocket onclose fires asynchronously after
    // stop() completes, so without this guard we'd incorrectly schedule a
    // reconnect every time a user logs out.
    if (this.isStopped) {
      this.log.debug('⛔ Connection closed after stop() — skipping reconnect');
      return;
    }

    // Do NOT schedule a reconnect if the DB has been paused (e.g. yieldLeadership called
    // disconnect() which triggered this onclose). Reconnecting would open a WebSocket,
    // call sendInitMessage() → exec() on a paused DB and crash.
    // Use databaseService.isPaused (ground truth) rather than connectionState — the WS
    // may close while connectionState is still 'connecting' (set before ws creation),
    // which would incorrectly pass a connectionState==='paused' check.
    if (this.databaseService.isPaused) {
      this.log.info(
        '⏸ Connection closed while DB is paused — skipping reconnect and resetting state',
      );
      this.connectionState.set('paused');
      return;
    }

    this.connectionState.set('disconnected');

    // Schedule reconnect
    this.scheduleReconnect();
  }

  /**
   * Handle connection error
   */
  private handleError(err: Event): void {
    this.log.error('WebSocket error:', err);
    this.connectionState.set('error');
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private scheduleReconnect(): void {
    // Never reconnect while paused — the DB is down and sendInitMessage will crash.
    // Use databaseService.isPaused (ground truth) — connectionState may lag behind.
    if (this.databaseService.isPaused) {
      this.log.info('⏸ Skipping reconnect — DB is paused');
      return;
    }

    if (this.reconnectAttempt >= this.maxReconnectAttempts) {
      this.log.error('Max reconnect attempts reached - giving up');
      this.connectionState.set('error');
      this.error.set('Connection failed - please refresh');
      return;
    }

    // Clear any existing reconnect timer before scheduling a new one.
    // Without this guard, two callers (e.g. an explicit call + handleClose)
    // can schedule overlapping reconnects — each triggering its own WS
    // connection and server-side token-refresh request.
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s, 60s (max)
    const delay = Math.min(1000 * 2 ** this.reconnectAttempt, 60000);
    this.reconnectAttempt++;

    this.log.info(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempt})...`);

    this.reconnectTimer = window.setTimeout(() => {
      this.connect().catch((error) => {
        // Connection failed - scheduleReconnect will be called via handleClose
        this.log.debug('Reconnection attempt failed:', error.message);
      });
    }, delay);
  }

  /**
   * Handle browser online event
   */
  private handleOnline(): void {
    // Don't reconnect if paused — DB is down.
    if (this.databaseService.isPaused) {
      this.log.info('⏸ Browser online but DB is paused — skipping reconnect');
      return;
    }

    this.log.info('Browser online - reconnecting');
    this.connectionState.set('disconnected');
    this.reconnectAttempt = 0; // Reset backoff
    this.connect().catch((error) => {
      // Connection failed - scheduleReconnect will be called via handleClose
      this.log.debug('Online reconnection attempt failed:', error.message);
    });
  }

  /**
   * Handle browser offline event
   */
  private handleOffline(): void {
    this.log.warn('Browser offline');
    this.connectionState.set('offline');
    this.disconnect();
  }

  /**
   * Disconnect from sync server
   */
  private disconnect(): void {
    this.stopTimers();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Process message queue sequentially
   * Optimized: Batches consecutive download messages for efficiency
   */
  private async processMessageQueue(): Promise<void> {
    // Already processing - will be picked up on next iteration
    if (this.processingQueue) {
      this.log.debug(
        `Queue processing already in progress (${this.messageQueue.length} messages queued)`,
      );
      return;
    }

    this.log.debug(`Starting message queue processing (${this.messageQueue.length} messages)`);
    this.processingQueue = true;

    while (this.messageQueue.length > 0) {
      const msg = this.messageQueue.shift()!;
      const msgStart = performance.now();

      // OPTIMIZATION: Batch consecutive download messages
      if (msg.type === 'download') {
        const batchedChanges = [...(msg as SyncDownloadMessage).changes];
        let batchCount = 1;

        // Collect all consecutive download messages from queue
        while (this.messageQueue.length > 0 && this.messageQueue[0].type === 'download') {
          const nextDownload = this.messageQueue.shift() as SyncDownloadMessage;
          batchedChanges.push(...nextDownload.changes);
          batchCount++;
        }

        this.log.debug(
          `Processing ${batchCount} batched download messages (${batchedChanges.length} changes total, ${this.messageQueue.length} remaining)`,
        );

        try {
          await this.handleDownload({ type: 'download', changes: batchedChanges });
          const msgTime = (performance.now() - msgStart).toFixed(0);
          this.log.debug(`Batched download processed in ${msgTime}ms`);
        } catch (err) {
          this.log.error('Batched download handling failed:', err);
        }
      } else {
        // Non-download messages: process normally
        this.log.debug(
          `Processing message type: ${msg.type} (${this.messageQueue.length} remaining)`,
        );

        try {
          await this.handleMessage(msg);
          const msgTime = (performance.now() - msgStart).toFixed(0);
          this.log.debug(`Message ${msg.type} processed in ${msgTime}ms`);
        } catch (err) {
          this.log.error(`Message ${msg.type} handling failed:`, err);
        }
      }
    }

    this.log.debug('Message queue processing complete');
    this.processingQueue = false;
  }

  /**
   * Handle incoming message
   */
  private async handleMessage(msg: SyncMessage): Promise<void> {
    switch (msg.type) {
      case 'clock_pong':
        this.clockSync.handlePong(msg);
        break;

      case 'schema':
        await this.handleSchema(msg as SyncSchemaMessage);
        break;

      case 'migration':
        await this.handleMigration(msg as SyncMigrationMessage);
        break;

      case 'ack':
        await this.handleAck(msg as SyncAckMessage);
        break;

      case 'download':
        await this.handleDownload(msg as SyncDownloadMessage);
        break;

      case 'download_complete':
        await this.handleDownloadComplete(msg as SyncDownloadCompleteMessage);
        break;

      case 'conflict':
        await this.handleConflict(msg as SyncConflictMessage);
        break;

      case 'error':
        this.log.error('Server error:', msg);
        this.error.set(msg.message);
        break;

      case 'token_refresh':
        await this.handleTokenRefresh(msg as SyncTokenRefreshMessage);
        break;

      default:
        this.log.warn('Unknown message type:', (msg as any).type);
    }
  }

  /**
   * Handle schema message (initial sync only)
   */
  private async handleSchema(msg: SyncSchemaMessage): Promise<void> {
    const schemaTime = performance.now();
    this.log.debug(
      `T+${(performance.now() - schemaTime).toFixed(0)}ms: RECEIVED schema DDL (${msg.ddl.length} bytes)`,
    );
    this.log.info('📋 Applying schema from server');

    try {
      this.log.debug(
        `T+${(performance.now() - schemaTime).toFixed(0)}ms: Calling changeApplier.applySchema()...`,
      );
      await this.changeApplier.applySchema(msg.ddl);
      this.log.info(
        `T+${(performance.now() - schemaTime).toFixed(0)}ms: ✅ Schema created successfully`,
      );

      // A database built from scratch by sync never had the untracked-blob bug,
      // and every blob it holds came from the server — skip the repair for good.
      await this.markBlobSyncRepairDone();

      // Send acknowledgment to server that schema is applied
      this.log.debug(
        `T+${(performance.now() - schemaTime).toFixed(0)}ms: Sending schema_applied acknowledgment`,
      );
      this.send({
        type: 'schema_applied',
        client_id: this.clientId,
      });

      // Mark schema as applied and resolve BOTH promises
      this.schemaApplied = true;

      this.log.debug(
        `T+${(performance.now() - schemaTime).toFixed(0)}ms: Resolving promises (resolveSchema=${!!this.resolveSchema}, resolveConnection=${!!this.resolveConnection})`,
      );

      if (this.resolveSchema) {
        this.resolveSchema();
        this.log.info(
          `T+${(performance.now() - schemaTime).toFixed(0)}ms: 🔓 Schema promise resolved`,
        );
      }

      // CRITICAL: Also resolve the connection promise now that schema is ready
      if (this.resolveConnection) {
        this.resolveConnection();
        this.log.info(
          `T+${(performance.now() - schemaTime).toFixed(0)}ms: 🔓 Connection promise resolved - app can proceed`,
        );
      }
    } catch (error: any) {
      this.log.error('❌ Schema application failed:', error);

      // Check if this is a fatal database error
      const isFatal =
        error.message &&
        (error.message.includes('unable to open database') ||
          error.message.includes('memory access out of bounds') ||
          error.message.includes('file not found') ||
          error.message.includes('Cannot read properties'));

      if (isFatal) {
        this.log.error('💀 FATAL DATABASE ERROR - stopping sync service');
        this.stop(); // Stop sync service immediately (this resets error to null)

        // Set error AFTER stop() so it is not cleared by stop()'s signal reset
        this.error.set(
          `Database corrupted: ${error.message}. Please reload the page or reset your local database.`,
        );

        // Show error to user
        this.toast.error('Database error detected. Please reload the page.', false);
      } else {
        this.error.set(`Schema error: ${error.message}`);
      }

      // Reject BOTH promises - app initialization will fail
      if (this.rejectSchema) {
        this.rejectSchema(error);
      }
      if (this.rejectConnection) {
        this.rejectConnection(error);
      }

      // Fatal error - database is unusable
      throw error;
    }
  }

  /**
   * Handle migration message
   */
  private async handleMigration(msg: SyncMigrationMessage): Promise<void> {
    this.log.info(`📥 Received ${msg.migrations.length} migration(s)`);

    try {
      await this.migrationApplier.applyMigrations(msg.migrations);
      this.log.info('✅ Migrations applied successfully');
    } catch (error: any) {
      this.log.error('❌ Migration failed:', error);
      this.error.set(`Migration failed: ${error.message}`);

      // Fatal error - user should refresh
      throw error;
    }
  }

  /**
   * Handle token_refresh message
   * The server sends this when a migration that modifies role_permissions has
   * been applied. We call AuthService to re-issue the JWT with fresh permissions
   * so the user sees their updated access rights without having to log out.
   */
  private async handleTokenRefresh(msg: SyncTokenRefreshMessage): Promise<void> {
    this.log.info(`🔄 Received token_refresh request (reason: ${msg.reason})`);
    await this.authService.refreshToken();
  }

  /**
   * Handle ACK message - mark changes as synced
   */
  private async handleAck(msg: SyncAckMessage): Promise<void> {
    this.log.debug('ACK message received:', msg);

    if (!msg.success) {
      this.log.error('Upload failed on server:', msg.error);
      this.error.set(`Upload failed: ${msg.error}`);
      return;
    }

    // NOTE: We do NOT advance the cursor here from the ACK's latest_log_id.
    // The server sends an incremental sync back to the uploader after every upload,
    // so the cursor is advanced via the normal handleDownload/handleDownloadComplete
    // path. Advancing it here could skip interleaved rows from other clients.

    // Extract change IDs from message_id (format: "upload-id1,id2,id3")
    const changeIds = this.extractChangeIds(msg.message_id);
    if (changeIds.length > 0) {
      try {
        await this.databaseService.markChangesSynced(changeIds);
        this.log.info(`✅ Marked ${changeIds.length} changes as synced`);
        this.pendingChanges.set(Math.max(0, this.pendingChanges() - changeIds.length));
      } catch (error: any) {
        this.log.error('Failed to mark changes as synced:', error);
      }
    }
  }

  /**
   * Handle download message
   */
  private async handleDownload(msg: SyncDownloadMessage): Promise<void> {
    this.log.info(`📥 Received ${msg.changes.length} changes`);

    // Track received rows for full-sync reconciliation
    if (this.fullSyncReceivedRows !== null) {
      for (const change of msg.changes) {
        if (change.operation !== 'DELETE') {
          if (!this.fullSyncReceivedRows.has(change.table)) {
            this.fullSyncReceivedRows.set(change.table, new Set());
          }
          this.fullSyncReceivedRows.get(change.table)!.add(change.row_id);
        }
      }
    }

    try {
      const appliedCount = await this.changeApplier.applyChanges(msg.changes);
      this.log.info(`✅ Applied ${appliedCount}/${msg.changes.length} changes`);

      // CRITICAL: Only advance the cursor AFTER changes are successfully written.
      // If this is done before applyChanges, a write failure will advance the cursor
      // past unwritten rows — the server will then never resend them, causing permanent
      // data loss on this client.
      if (msg.latest_log_id != null) {
        await this.updateLastLogId(msg.latest_log_id);
      }
    } catch (error: any) {
      this.log.error('❌ Download failed:', error);

      // Check if this is a fatal database error
      const isFatal =
        error.message &&
        (error.message.includes('memory access out of bounds') ||
          error.message.includes('unable to open database') ||
          error.message.includes('database is locked'));

      if (isFatal) {
        this.log.error('💀 FATAL ERROR during change application - stopping sync');
        this.stop(); // Stop sync immediately (this resets error to null)

        // Set error AFTER stop() so it is not cleared by stop()'s signal reset
        this.error.set(
          `Database corrupted during sync. Please reload the page or reset your local database.`,
        );
        this.toast.error('Database corruption detected. Please reload the page.', false);
      }

      throw error;
    }

    // Emit event for UI to refresh (if needed)
    // TODO: Add event emitter or signal for data changes
  }

  /**
   * Handle download_complete message
   * Signals that initial sync download is complete
   */
  private async handleDownloadComplete(msg: SyncDownloadCompleteMessage): Promise<void> {
    this.log.info(
      `✅ Initial download complete: ${msg.total_rows} total rows (sync_type: ${msg.sync_type})`,
    );
    this.log.debug(
      `Download state: downloadComplete=${this.downloadComplete}, hasResolveCallback=${!!this.resolveDownload}`,
    );

    // CURSOR INTEGRITY CHECK (incremental syncs only):
    // The server sends the table+row_id of a known row at the client's claimed cursor
    // position. If that row is missing locally, the cursor is stale — data was never
    // written despite the cursor being advanced. Reset to 0 and reconnect for a full
    // re-sync. This is safe: outgoing _sync_log (pending uploads) is unaffected.
    if (msg.sync_type === 'incremental' && (msg.cursor_verify || msg.cursor_verify_batch?.length)) {
      const verificationSet =
        msg.cursor_verify_batch && msg.cursor_verify_batch.length > 0
          ? msg.cursor_verify_batch
          : msg.cursor_verify
            ? [msg.cursor_verify]
            : [];

      this.log.debug(
        `Cursor integrity check: verifying ${verificationSet.length} probe row(s) across table(s)`,
      );

      if (msg.cursor_verify_checksum && verificationSet.length > 0) {
        const computedChecksum = await this.computeCursorVerifyChecksum(verificationSet);
        if (computedChecksum !== msg.cursor_verify_checksum) {
          this.incrementSyncObservabilityCounter(
            this.syncObservabilityKeys.verifyChecksumMismatches,
          );
          this.recordSyncIntegrityReason('checksum_mismatch');
          this.log.warn(
            `⚠️ CURSOR VERIFY CHECKSUM MISMATCH: server=${msg.cursor_verify_checksum}, client=${computedChecksum}`,
            { reason_code: 'checksum_mismatch' },
          );
        } else {
          this.log.debug('Cursor verify checksum matched ✓');
        }
      }

      try {
        const missingRows: Array<{ table: string; row_id: string; pk_col: string }> = [];

        for (const probe of verificationSet) {
          const pkCol = probe.pk_col ?? 'id';
          const rows = await this.databaseService.exec(
            `SELECT 1 as found FROM ${probe.table} WHERE ${pkCol} = ?`,
            [probe.row_id],
          );
          if (!rows || rows.length === 0) {
            missingRows.push({ table: probe.table, row_id: probe.row_id, pk_col: pkCol });
          }
        }

        if (missingRows.length > 0) {
          this.incrementSyncObservabilityCounter(this.syncObservabilityKeys.verifyBatchFailures);
          this.incrementSyncObservabilityCounter(this.syncObservabilityKeys.forcedFullResyncCount);
          this.recordSyncIntegrityReason('missing_probe_rows');
          const missingSummary = missingRows
            .slice(0, 5)
            .map((row) => `${row.table}.${row.row_id}`)
            .join(', ');
          this.log.warn(
            `⚠️ CURSOR INTEGRITY FAILURE: ${missingRows.length}/${verificationSet.length} probe row(s) missing locally ` +
              `(${missingSummary}). Resetting cursor to 0 and reconnecting for full re-sync.`,
            { reason_code: 'missing_probe_rows', missing_count: missingRows.length },
          );
          await this.updateLastLogId(0);
          // Reset all promises so the next reconnect has fresh ones to resolve.
          // Without this, downloadPromise stays permanently rejected and any
          // subsequent doStart() call fails immediately on the settled promise.
          this.resetAllPromises();
          // Disconnect — handleClose() will call scheduleReconnect() exactly
          // once with proper backoff. Do NOT call scheduleReconnect() here too;
          // doing so would schedule two overlapping reconnects, each causing a
          // server-side token-refresh request.
          this.disconnect();
          return;
        }

        this.log.debug(
          `Cursor integrity check passed: ${verificationSet.length}/${verificationSet.length} probe row(s) present ✓`,
        );

        if (msg.table_digests && msg.table_digests.length > 0) {
          const mismatchedDigests: Array<{
            table: string;
            row_id: string;
            expected: string;
            actual: string;
          }> = [];

          for (const digestEntry of msg.table_digests) {
            const pkCol = digestEntry.pk_col ?? 'id';
            const rows = await this.databaseService.exec(
              `SELECT * FROM ${digestEntry.table} WHERE ${pkCol} = ?`,
              [digestEntry.row_id],
            );

            if (!rows || rows.length === 0) {
              mismatchedDigests.push({
                table: digestEntry.table,
                row_id: digestEntry.row_id,
                expected: digestEntry.row_digest,
                actual: 'missing',
              });
              continue;
            }

            const localDigest = await this.computeRowDigest(rows[0]);
            if (localDigest !== digestEntry.row_digest) {
              mismatchedDigests.push({
                table: digestEntry.table,
                row_id: digestEntry.row_id,
                expected: digestEntry.row_digest,
                actual: localDigest,
              });
            }
          }

          if (mismatchedDigests.length > 0) {
            this.incrementSyncObservabilityCounter(
              this.syncObservabilityKeys.tableDigestMismatches,
            );
            const mismatchSummary = mismatchedDigests
              .slice(0, 5)
              .map((row) => `${row.table}.${row.row_id}`)
              .join(', ');

            if (!this.repairAttemptedThisCycle) {
              this.repairAttemptedThisCycle = true;
              this.recordSyncIntegrityReason('table_digest_mismatch_repair_requested');
              this.log.warn(
                `⚠️ TABLE DIGEST MISMATCH: ${mismatchedDigests.length}/${msg.table_digests.length} digest assertion(s) mismatched ` +
                  `(${mismatchSummary}). Requesting targeted repair before full re-sync.`,
                {
                  reason_code: 'table_digest_mismatch_repair_requested',
                  mismatch_count: mismatchedDigests.length,
                },
              );
              this.requestTargetedRepair(
                mismatchedDigests.map((row) => ({
                  table: row.table,
                  row_id: row.row_id,
                })),
                msg.latest_log_id,
              );
              return;
            }

            this.incrementSyncObservabilityCounter(
              this.syncObservabilityKeys.forcedFullResyncCount,
            );
            this.recordSyncIntegrityReason('table_digest_mismatch_full_resync');
            this.log.warn(
              `⚠️ TABLE DIGEST MISMATCH: ${mismatchedDigests.length}/${msg.table_digests.length} digest assertion(s) mismatched ` +
                `(${mismatchSummary}). Targeted repair already attempted in this connection cycle; resetting cursor to 0 and reconnecting for full re-sync.`,
              {
                reason_code: 'table_digest_mismatch_full_resync',
                mismatch_count: mismatchedDigests.length,
              },
            );
            await this.updateLastLogId(0);
            this.resetAllPromises();
            this.disconnect();
            return;
          }

          this.log.debug(
            `Table digest check passed: ${msg.table_digests.length}/${msg.table_digests.length} assertion(s) matched ✓`,
          );
        }
      } catch (err: any) {
        this.incrementSyncObservabilityCounter(this.syncObservabilityKeys.verifyBatchFailures);
        this.incrementSyncObservabilityCounter(this.syncObservabilityKeys.forcedFullResyncCount);
        this.recordSyncIntegrityReason('verify_query_failed');
        // Query failed — most likely "no such table", meaning the schema itself
        // is missing or corrupt (e.g. Safari cleared IndexedDB partial state).
        // We cannot verify the cursor AND the schema may be gone.
        // Reset BOTH the data cursor AND the migration version so the server
        // will resend the full schema DDL + all data on the next connection.
        this.log.warn(
          `⚠️ CURSOR INTEGRITY CHECK ERROR: could not verify cursor probe set — ` +
            `resetting cursor AND migrations to 0, reconnecting for full schema+data re-sync.`,
          { reason_code: 'verify_query_failed' },
          err,
        );
        await this.updateLastLogId(0);
        // Clear _migrations so next init message sends last_sync_version=0,
        // which tells the server to resend the full customer.sql schema DDL
        // before any data. Without this, the server skips the schema and data
        // writes fail on tables that no longer exist.
        try {
          await this.databaseService.exec('DELETE FROM _migrations');
          this.log.warn('⚠️ Cleared _migrations table to force full schema resend on reconnect');
        } catch {
          // Table might also be gone — that's fine, version will be 0 anyway
          this.log.debug('_migrations table not found (already gone) — version will be 0');
        }
        // Schema will be resent from scratch — must clear schemaApplied so the
        // next doStart() waits for the incoming schema DDL instead of proceeding
        // immediately and querying tables that no longer exist.
        this.schemaApplied = false;
        // Reset all promises (schema + download) so the next reconnect gets
        // fresh ones. Without this, both promises stay permanently rejected and
        // every subsequent doStart() call fails instantly.
        this.resetAllPromises();
        // Disconnect — handleClose() will schedule the single reconnect.
        // Do NOT call scheduleReconnect() here; that would create two overlapping
        // timers, each spawning a connection and triggering a token-refresh.
        this.disconnect();
        return;
      }
    }

    // Mark download as complete FIRST - this lets the app proceed immediately
    // Reconciliation and cursor updates can happen in the background
    this.downloadComplete = true;

    // CRITICAL: Resolve the download promise immediately so app can proceed
    // wa-sqlite writes are already committed by the time we get here
    this.log.debug('Resolving download promise - app can proceed');
    if (this.resolveDownload) {
      this.resolveDownload();
      this.log.info('🔓 Download promise RESOLVED - app can proceed');
    } else {
      this.log.warn('No resolveDownload callback found!');
    }

    // Store the sync cursor (non-blocking for app startup)
    if (msg.latest_log_id != null) {
      this.updateLastLogId(msg.latest_log_id).catch((err) => {
        this.log.error('Failed to update sync cursor:', err);
      });
    }

    if (msg.table_watermarks && msg.table_watermarks.length > 0) {
      this.updateTableWatermarks(msg.table_watermarks).catch((err) => {
        this.log.error('Failed to update table watermarks:', err);
      });
    }

    // Full-sync reconciliation: delete local rows not received from server
    // Run in background - this is cleanup, not blocking for app startup
    if (msg.sync_type === 'full' && this.fullSyncReceivedRows !== null) {
      const receivedRows = this.fullSyncReceivedRows;
      this.fullSyncReceivedRows = null;

      // Run reconciliation async without blocking
      this.reconcileAfterFullSync(receivedRows).catch((err) => {
        this.log.error('Full sync reconciliation failed:', err);
      });
    } else {
      this.fullSyncReceivedRows = null;
    }
  }

  /**
   * Trigger upload with debouncing to batch rapid changes
   */
  triggerUpload(): void {
    // Only trigger if connected
    if (!this.isConnected()) {
      this.log.debug('Upload triggered but not connected - changes will sync on reconnect');
      return;
    }

    // Clear existing debounce timer
    if (this.uploadDebounceTimer) {
      clearTimeout(this.uploadDebounceTimer);
    }

    // Debounce to batch rapid changes (500ms)
    this.uploadDebounceTimer = window.setTimeout(() => {
      this.uploadPendingChanges();
    }, 500);
  }

  /**
   * Start periodic clock resync timer
   */
  private startResyncTimer(): void {
    this.resyncTimer = setInterval(async () => {
      if (this.clockSync.needsResync() && this.connected()) {
        await this.clockSync.sync(this.ws!);
      }
    }, 60000); // Check every minute
  }

  /**
   * Stop all timers
   */
  private stopTimers(): void {
    if (this.uploadDebounceTimer) {
      clearTimeout(this.uploadDebounceTimer);
      this.uploadDebounceTimer = undefined;
    }

    if (this.resyncTimer) {
      clearInterval(this.resyncTimer);
      this.resyncTimer = null;
    }
  }

  /**
   * Upload pending changes to server
   */
  private async uploadPendingChanges(): Promise<void> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.log.debug('Cannot upload - WebSocket not connected');
      return;
    }

    try {
      // Get pending changes from _sync_log
      const pendingChanges = await this.databaseService.getPendingSyncChanges();

      if (pendingChanges.length === 0) {
        this.log.debug('No pending changes to upload');
        this.pendingChanges.set(0);
        return;
      }

      this.log.info(`📤 Uploading ${pendingChanges.length} pending changes...`);
      this.pendingChanges.set(pendingChanges.length);

      // Convert to ChangeRecord format
      const changes: any[] = pendingChanges.map((log: any) => ({
        table: log.table_name,
        row_id: log.row_id,
        // form_blobs uses 'hash' as primary key, not 'id'
        ...(log.table_name === 'form_blobs' ? { pk_col: 'hash' } : {}),
        operation: log.operation,
        data: log.row_data ? JSON.parse(log.row_data) : null,
        timestamp: log.timestamp,
        version: 0, // Version tracking happens on server
      }));

      // Create message ID with change IDs for ACK tracking
      const changeIds = pendingChanges.map((log: any) => log.id).join(',');
      const messageId = `upload-${changeIds}`;

      // Read current sync cursor to tell the server where to start gap-fill from
      let uploadLastLogId = 0;
      try {
        const logResult = await this.databaseService.exec(
          `SELECT value FROM _sync_metadata WHERE key = 'last_sync_log_id'`,
        );
        if (logResult?.length > 0 && logResult[0].value != null) {
          uploadLastLogId = parseInt(logResult[0].value as string, 10);
        }
      } catch {
        uploadLastLogId = 0;
      }

      // Send upload message
      this.send({
        type: 'upload',
        message_id: messageId,
        client_id: this.clientId,
        last_log_id: uploadLastLogId,
        changes,
      });

      this.log.info(`✅ Upload message sent (${changes.length} changes)`);
      this.syncing.set(true);

      // syncing flag will be cleared when ACK is received
      setTimeout(() => this.syncing.set(false), 5000); // Timeout after 5s
    } catch (error: any) {
      this.log.error('Upload failed:', error);
      this.error.set(`Upload failed: ${error.message}`);
    }
  }

  /**
   * Send initialization message to server
   * This triggers the server to send schema (if needed) and initial data
   */
  private async sendInitMessage(): Promise<void> {
    try {
      // CRITICAL: Database MUST be initialized before calling this method
      // If it's not, that's a fatal programming error, not an expected case
      if (this.databaseService.isFailed) {
        throw new Error('FATAL: Database is in failed state - cannot send init message');
      }

      // Get schema version and last sync version from database.
      // On first sync the DB is empty — no tables exist yet; schema arrives after
      // the server processes our init message. Check sqlite_master first so we can
      // skip all three table queries without throwing "no such table" exceptions.
      let schemaVersion = 0;
      let lastSyncVersion = 0;

      const tableCountResult = await this.databaseService.exec(
        `SELECT count(*) as cnt FROM sqlite_master WHERE type='table'`,
      );
      const tableCount = tableCountResult?.[0]?.cnt ?? 0;

      if (tableCount === 0) {
        // Fresh database — no schema yet. Skip the individual table queries entirely.
        this.log.info('Empty database detected - first sync, sending version 0');
      } else {
        // Schema already exists — read actual versions.
        try {
          const schemaResult = await this.databaseService.exec(
            'SELECT MAX(version) as version FROM _schema_version',
          );
          if (!schemaResult) {
            throw new Error(
              'FATAL: Database exec() returned undefined - database not properly initialized',
            );
          }
          if (schemaResult.length > 0 && schemaResult[0].version != null) {
            schemaVersion = schemaResult[0].version;
          }
        } catch (err: any) {
          if (err.message?.includes('no such table') || err.message?.includes('_schema_version')) {
            this.log.info('Schema version table not found - treating as first sync (version 0)');
            schemaVersion = 0;
          } else {
            this.log.error('FATAL: Failed to read schema version:', err);
            throw new Error(`Failed to read schema version: ${err.message || String(err)}`);
          }
        }

        try {
          const migrationResult = await this.databaseService.exec(
            'SELECT MAX(version) as version FROM _migrations',
          );
          if (!migrationResult) {
            throw new Error(
              'FATAL: Database exec() returned undefined - database not properly initialized',
            );
          }
          if (migrationResult.length > 0 && migrationResult[0].version != null) {
            lastSyncVersion = migrationResult[0].version;
          }
        } catch (err: any) {
          if (err.message?.includes('no such table') || err.message?.includes('_migrations')) {
            this.log.info('Migrations table not found - treating as first sync (version 0)');
            lastSyncVersion = 0;
          } else {
            this.log.error('FATAL: Failed to read migration version:', err);
            throw new Error(`Failed to read migration version: ${err.message || String(err)}`);
          }
        }
      }

      this.log.debug('Sending init message:', {
        client_id: this.clientId,
        schema_version: schemaVersion,
        last_sync_version: lastSyncVersion,
      });

      // Read last sync log position from _sync_metadata.
      // Only query if the DB has tables; on a fresh DB the table doesn't exist yet.
      let lastLogId = 0;
      if (tableCount > 0) {
        try {
          const logResult = await this.databaseService.exec(
            `SELECT value FROM _sync_metadata WHERE key = 'last_sync_log_id'`,
          );
          if (logResult?.length > 0 && logResult[0].value != null) {
            lastLogId = parseInt(logResult[0].value as string, 10);
          }
        } catch {
          // Table might not exist yet (e.g. schema exists but _sync_metadata was added later)
          lastLogId = 0;
        }
      }

      this.log.debug('Last log id:', lastLogId);
      const tableWatermarks = await this.readTableWatermarks();

      this.send({
        type: 'init',
        client_id: this.clientId,
        schema_version: schemaVersion,
        last_sync_version: lastSyncVersion,
        last_log_id: lastLogId,
        ...(tableWatermarks.length > 0 ? { table_watermarks: tableWatermarks } : {}),
      });

      this.log.info('📤 Init message sent to server');
    } catch (error: any) {
      this.log.error('Failed to send init message:', error);
      throw error;
    }
  }

  /**
   * Update the stored sync log cursor in _sync_metadata.
   */
  private async updateLastLogId(logId: number): Promise<void> {
    try {
      await this.databaseService.exec(
        `INSERT INTO _sync_metadata (key, value) VALUES ('last_sync_log_id', ?)
         ON CONFLICT(key) DO UPDATE SET value = ?`,
        [String(logId), String(logId)],
      );
    } catch (error: any) {
      this.log.error('Failed to update last_sync_log_id:', error);
    }
  }

  private async readTableWatermarks(): Promise<Array<{ table: string; last_log_id: number }>> {
    try {
      const rows = await this.databaseService.exec(
        `SELECT key, value FROM _sync_metadata WHERE key LIKE 'last_sync_log_id:%'`,
      );

      return (rows ?? [])
        .map((row) => {
          const key = row['key'] as string;
          const value = row['value'] as string;
          const table = key?.startsWith('last_sync_log_id:')
            ? key.slice('last_sync_log_id:'.length)
            : '';
          const parsed = Number.parseInt(value, 10);
          if (!table || !Number.isFinite(parsed)) {
            return null;
          }
          return { table, last_log_id: parsed };
        })
        .filter((row): row is { table: string; last_log_id: number } => row !== null)
        .sort((a, b) => a.table.localeCompare(b.table));
    } catch {
      return [];
    }
  }

  private async updateTableWatermarks(
    tableWatermarks: Array<{ table: string; last_log_id: number }>,
  ): Promise<void> {
    for (const watermark of tableWatermarks) {
      if (!watermark.table) {
        continue;
      }
      const logId = Math.max(0, Math.trunc(watermark.last_log_id));
      const metadataKey = `last_sync_log_id:${watermark.table}`;
      await this.databaseService.exec(
        `INSERT INTO _sync_metadata (key, value) VALUES (?, ?)
         ON CONFLICT(key) DO UPDATE SET value = ?`,
        [metadataKey, String(logId), String(logId)],
      );
    }
  }

  private async computeCursorVerifyChecksum(
    probes: Array<{ table: string; row_id: string; pk_col?: string }>,
  ): Promise<string> {
    const normalized = probes
      .map((probe) => ({
        table: probe.table,
        row_id: probe.row_id,
        pk_col: probe.pk_col ?? 'id',
      }))
      .sort((a, b) => {
        if (a.table === b.table) {
          return a.row_id.localeCompare(b.row_id);
        }
        return a.table.localeCompare(b.table);
      });

    const payload = JSON.stringify(normalized);
    const encoded = new TextEncoder().encode(payload);
    const digest = await crypto.subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  private async computeRowDigest(row: Record<string, unknown>): Promise<string> {
    const canonical = JSON.stringify(this.canonicalizeForDigest(row));
    const encoded = new TextEncoder().encode(canonical);
    const digest = await crypto.subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  private canonicalizeForDigest(value: unknown): unknown {
    if (Array.isArray(value)) {
      return value.map((item) => this.canonicalizeForDigest(item));
    }
    if (value && typeof value === 'object') {
      const input = value as Record<string, unknown>;
      const sortedKeys = Object.keys(input).sort();
      const result: Record<string, unknown> = {};
      for (const key of sortedKeys) {
        result[key] = this.canonicalizeForDigest(input[key]);
      }
      return result;
    }
    return value;
  }

  private incrementSyncObservabilityCounter(key: string): void {
    const currentRaw = this.localStorage.getItem(key);
    const current = currentRaw ? parseInt(currentRaw, 10) : 0;
    const next = Number.isFinite(current) ? current + 1 : 1;
    this.localStorage.setItem(key, String(next));
  }

  private recordSyncIntegrityReason(reason: string): void {
    this.localStorage.setItem(this.syncObservabilityKeys.lastIntegrityReason, reason);
    this.localStorage.setItem(this.syncObservabilityKeys.lastIntegrityAt, new Date().toISOString());
  }

  private requestTargetedRepair(
    probes: Array<{ table: string; row_id: string; pk_col?: string }>,
    latestLogId: number,
  ): void {
    const message: SyncRepairRequestMessage = {
      type: 'repair_request',
      request_id: `repair-${Date.now()}-${nanoid(6)}`,
      last_log_id: latestLogId,
      reason: 'table_digest_mismatch',
      probes,
    };
    this.send(message);
  }

  /**
   * After a full sync, delete local rows that the server didn't send
   * (they were deleted on the server while this client was offline)
   *
   * CRITICAL FIX for Bug #2 (fullSyncReceivedRows Race Condition):
   * Add defensive check to prevent deleting all rows if sync looks incomplete.
   * If reconciliation receives an empty or incomplete receivedRows map,
   * it could indicate a failed sync or race condition - don't delete anything.
   */
  private async reconcileAfterFullSync(receivedRows: Map<string, Set<string>>): Promise<void> {
    const startTime = performance.now();
    this.log.debug('🧹 Starting full-sync reconciliation...');

    // DEFENSIVE CHECK: Prevent deletes if sync looks incomplete
    // If this is a full sync but no rows were received, that's a sign something went wrong
    // Don't delete all local rows - this prevents data loss from incomplete syncs
    if (!receivedRows || receivedRows.size === 0) {
      this.log.warn(
        '⚠️❌ Reconciliation SKIPPED - no records received in this sync (incomplete sync detected)',
      );
      this.log.warn(
        '⚠️❌ This looks like a race condition or sync failure - not deleting any rows to prevent data loss',
      );
      return; // Exit without deleting anything
    }

    // Internal tables that must never be touched by reconciliation.
    // Blacklist approach: new application tables are reconciled automatically.
    const EXCLUDED_TABLES = new Set([
      '_schema_version',
      '_migrations',
      '_sync_log',
      '_sync_metadata',
      '_sync_conflicts',
    ]);

    // Discover all tables in the local DB at runtime
    let allTables: string[] = [];
    try {
      const tableRows = await this.databaseService.exec(
        `SELECT name FROM sqlite_master WHERE type = 'table' ORDER BY name`,
      );
      allTables = (tableRows ?? []).map((r) => r['name'] as string);
    } catch {
      this.log.warn('🧹 Reconciliation: could not list tables — skipping');
      return;
    }
    // Also exclude all SQLite-internal tables (sqlite_sequence, sqlite_stat1, etc.).
    // These have non-standard schemas and are never part of the sync protocol.
    const syncedTables = allTables.filter(
      (t) => !EXCLUDED_TABLES.has(t) && !t.startsWith('sqlite_'),
    );

    // Build per-table PK column map (most tables use 'id'; form_blobs uses 'hash', etc.)
    const tablePkCol = new Map<string, string>();
    for (const table of syncedTables) {
      try {
        const cols = await this.databaseService.exec(`PRAGMA table_info(${table})`);
        const pkCol = (cols ?? []).find((c) => (c['pk'] as number) === 1)?.['name'] as
          | string
          | undefined;
        tablePkCol.set(table, pkCol ?? 'id');
      } catch {
        tablePkCol.set(table, 'id');
      }
    }

    // Get pending uploads so we don't delete rows the client hasn't uploaded yet
    this.log.debug('🧹 Getting pending changes...');
    const pendingChanges = await this.databaseService.getPendingSyncChanges();
    const pendingRowIds = new Set(pendingChanges.map((c: any) => `${c.table_name}:${c.row_id}`));
    this.log.debug(`🧹 Found ${pendingChanges.length} pending changes`);

    let totalDeleted = 0;
    let totalChecked = 0;
    let lastLogTime = performance.now();

    for (const table of syncedTables) {
      const tableStart = performance.now();
      const receivedIds = receivedRows.get(table) ?? new Set();
      const pkCol = tablePkCol.get(table) ?? 'id';

      // Get all local row IDs using the correct PK column for this table
      let localRows: Array<Record<string, any>>;
      try {
        localRows = await this.databaseService.exec(`SELECT ${pkCol} AS _pk FROM ${table}`);
      } catch {
        // Table might not exist yet
        continue;
      }

      if (!localRows || localRows.length === 0) {
        continue;
      }

      let tableDeleted = 0;
      for (const row of localRows) {
        const rowId = row['_pk'] as string;
        totalChecked++;

        // Log progress every 100ms to detect infinite loops
        if (performance.now() - lastLogTime > 100) {
          this.log.debug(
            `[RECONCILE] 🔍 Progress: table=${table}, checked=${totalChecked}, deleted=${totalDeleted}`,
          );
          lastLogTime = performance.now();
        }

        if (!receivedIds.has(rowId) && !pendingRowIds.has(`${table}:${rowId}`)) {
          await this.databaseService.exec(`DELETE FROM ${table} WHERE ${pkCol} = ?`, [rowId]);
          totalDeleted++;
          tableDeleted++;
        }
      }

      const tableTime = (performance.now() - tableStart).toFixed(0);
      if (tableDeleted > 0 || localRows.length > 10) {
        this.log.debug(
          `🧹 ${table}: checked ${localRows.length} rows, deleted ${tableDeleted} (${tableTime}ms)`,
        );
      }
    }

    const totalTime = (performance.now() - startTime).toFixed(0);
    if (totalDeleted > 0) {
      this.log.info(
        `🧹 Full-sync reconciliation: deleted ${totalDeleted}/${totalChecked} stale local rows (${totalTime}ms)`,
      );
    } else {
      this.log.debug(
        `🧹 Full-sync reconciliation: no stale rows found (checked ${totalChecked} rows in ${totalTime}ms)`,
      );
    }
  }

  /**
   * Handle conflict message - server rejected our changes
   */
  private async handleConflict(msg: SyncConflictMessage): Promise<void> {
    for (const conflict of msg.conflicts) {
      // Apply the winning version locally (so client matches server)
      await this.changeApplier.applyChanges([
        {
          table: conflict.table,
          row_id: conflict.row_id,
          operation: 'UPDATE',
          data: conflict.winning_data,
          timestamp: conflict.winning_timestamp,
          version: 0,
        },
      ]);

      this.log.warn(
        `⚠️ Conflict: your change to ${conflict.table}.${conflict.row_id} was rejected`,
      );
    }

    // Show user notification
    if (msg.conflicts.length > 0) {
      this.toast.warning(
        `${msg.conflicts.length} change(s) conflicted with edits from another user. The newer version was kept.`,
      );
    }
  }

  /**
   * Send message to server
   */
  private send(message: any): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket not connected');
    }

    this.ws.send(JSON.stringify(message));
  }

  /**
   * Get or create client ID
   */
  private getOrCreateClientId(): string {
    const key = 'custom_sync_client_id';
    let clientId = this.localStorage.getItem(key);

    if (!clientId) {
      clientId = `client-${nanoid()}`;
      this.localStorage.setItem(key, clientId);
    }

    return clientId;
  }

  /**
   * Extract change IDs from message ID
   */
  private extractChangeIds(messageId: string): number[] {
    const match = messageId.match(/^upload-(.+)$/);
    if (!match) return [];

    return match[1].split(',').map(Number);
  }

  /**
   * Handle BroadcastChannel messages from other tabs
   */
  private handleBroadcastMessage(message: BroadcastMessage): void {
    switch (message.type) {
      case 'leader-elected':
        this.log.info('New leader elected:', message.leaderId);
        break;

      case 'data-changed':
        // Log entity changes for now - UI refresh logic comes later
        this.log.info('Data changed from leader:', {
          table: message.table,
          operation: message.operation,
          rowIds: message.rowIds,
        });
        break;

      case 'tab-focused': {
        // Another tab announced it now has user focus.
        // If this tab is currently the leader, yield so the focused tab can take over.
        // We deliberately do nothing if this tab is NOT the leader — the focused
        // tab will acquire the lock via its own scheduled start() attempt.
        const focusedTab = message.tabId;
        this.log.info(`Received tab-focused from tab ${focusedTab} | isLeader: ${this.isLeader}`);
        if (this.isLeader && focusedTab !== this.tabId) {
          this.log.info('Another tab has focus — yielding leadership');
          this.yieldLeadership();
        }
        break;
      }

      case 'request-leadership':
        this.log.info(
          'Received request-leadership from tab',
          message.requesterId,
          '| isLeader:',
          this.isLeader,
          '| state:',
          this.connectionState(),
        );
        if (this.isLeader && message.requesterId !== this.tabId) {
          this.log.info('Leadership requested by foreground tab — yielding');
          this.yieldLeadership();
        } else if (!this.isLeader) {
          this.log.info('Received request-leadership but this tab is not the leader — ignoring');
        }
        break;
    }
  }

  /**
   * Try to become the sync leader using Web Locks API
   * Returns true if became leader, false if another tab is already leader
   */
  private async tryBecomeLeader(): Promise<boolean> {
    // Check if Web Locks API is available (not available in test environment or older browsers)
    if (!navigator.locks) {
      this.log.warn('Web Locks API not available - becoming leader by default');
      return true;
    }

    return new Promise((resolve) => {
      navigator.locks.request('checkpoint-sync-leader', { ifAvailable: true }, (lock) => {
        if (!lock) {
          resolve(false);
          return; // releases immediately
        }

        // Store release callback for yielding leadership
        return new Promise<void>((releaseResolve) => {
          this.lockReleaseCallback = releaseResolve;
          resolve(true);
        });
      });
    });
  }

  /**
   * Yield leadership to another tab (typically when going to background)
   *
   * CRITICAL ORDERING: terminate the DB worker (releases OPFS SAH pool handles) BEFORE
   * releasing the Web Lock. If we released the lock first, the other tab could immediately
   * win it and try to open the SAH pool while our worker still holds it, causing this error:
   *   "Access Handles cannot be created if there is another open Access Handle"
   * This results in the other tab permanently failing to init the database and hitting /error.
   */
  private async yieldLeadership(): Promise<void> {
    if (this.lockReleaseCallback) {
      this.log.info('Yielding leadership - releasing Web Lock');
      // Capture and immediately clear the callback to prevent re-entry from concurrent
      // tab-focused / request-leadership broadcasts received while we are awaiting below.
      const releaseLock = this.lockReleaseCallback;
      this.lockReleaseCallback = undefined;
      this.isLeader = false;
      this.disconnect(); // closes WebSocket
      await this.databaseService.pauseForLeadership(); // terminates worker, releases SAH handles FIRST
      releaseLock(); // THEN release Web Lock — so the next leader can safely acquire the SAH pool
      this.connectionState.set('paused');
      this.leadershipCheckDelay = this.LEADERSHIP_CHECK_MIN_DELAY; // reset backoff after a clean yield
      this.watchForLeadershipOpportunity();
    }
  }

  /**
   * Watch for opportunities to become leader again.
   *
   * Uses exponential backoff (5s → 10s → 20s → 40s → 60s max) to avoid
   * hammering the Web Lock API on a permanently-paused background tab.
   * The fast path is the focus effect() in the constructor: when the user
   * switches to this tab, TabFocusService fires isFocused→true, the effect
   * broadcasts 'tab-focused' and schedules a 500ms lock attempt.
   */
  private watchForLeadershipOpportunity(): void {
    if (this.leadershipCheckTimer) {
      clearTimeout(this.leadershipCheckTimer);
      this.leadershipCheckTimer = undefined;
    }

    const delay = this.leadershipCheckDelay;
    const nextDelay = Math.min(delay * 2, this.LEADERSHIP_CHECK_MAX_DELAY);
    this.log.debug(`⏳ Leadership check in ${delay / 1000}s (next: ${nextDelay / 1000}s)`);

    // IMPORTANT: do NOT pre-acquire the lock here. doStart() handles lock acquisition.
    // Pre-acquiring here causes a double-lock bug (same-page ifAvailable=false on re-entry).
    this.leadershipCheckTimer = window.setTimeout(async () => {
      this.leadershipCheckTimer = undefined;

      // If we somehow became the leader already (e.g. via the focus effect fast path),
      // there's no need to run the scheduled check.
      if (this.isLeader) {
        return;
      }

      // Advance the backoff BEFORE calling start() — doStart() will call
      // watchForLeadershipOpportunity() again if the lock is still taken, and
      // it needs to see the updated delay.
      this.leadershipCheckDelay = nextDelay;

      // CRITICAL: call start() (not doStart()) so the startupPromise guard is set,
      // preventing a concurrent start() call from the route guard from running a
      // second doStart() in parallel — which would re-enter tryBecomeLeader(),
      // fail (same-page re-entry), and call pauseForLeadership() while the first
      // doStart() is mid-flight inside handleOpen()/sendInitMessage().
      this.log.info(
        `Checking for leadership opportunity (tab ${document.hidden ? 'hidden' : 'visible'}, next backoff ${nextDelay / 1000}s)...`,
      );
      await this.start();
    }, delay);
  }

  /**
   * Broadcast data change to other tabs (called by ChangeApplierService)
   */
  broadcastDataChange(table: string, operation: string, rowIds: string[]): void {
    if (this.isLeader && this.channel) {
      this.channel.postMessage({
        type: 'data-changed',
        table,
        operation,
        rowIds,
      });
    }
  }

  /**
   * Request leadership from the current leader tab (e.g. when user clicks "Use this tab")
   * Also directly starts polling so paused tab can acquire DB without waiting the full interval.
   */
  requestLeadership(): void {
    if (!this.isLeader && this.channel) {
      this.log.info('Requesting leadership from current leader');
      this.channel.postMessage({ type: 'request-leadership', requesterId: this.tabId });
    }
    // Cancel any pending backoff timer and reset the delay — the user just
    // brought the tab to the front so we want to try immediately at full speed.
    if (this.leadershipCheckTimer) {
      clearTimeout(this.leadershipCheckTimer);
      this.leadershipCheckTimer = undefined;
    }
    this.leadershipCheckDelay = this.LEADERSHIP_CHECK_MIN_DELAY;
    // Use start() (not doStart()) so the startupPromise guard prevents a concurrent
    // doStart() from tearing down the DB if the guard also calls start() at the same time.
    this.leadershipCheckTimer = window.setTimeout(async () => {
      this.leadershipCheckTimer = undefined;
      await this.start();
    }, 400) as unknown as number;
  }
}
